import styled, { useTheme } from "styled-components/macro";
import { Row } from "../../../../../../components/Layout/Layout";
import { H3 } from "../../../../../../components/Typography/Typography";
import { useTranslation } from "react-i18next";
import { useReducer, useState } from "react";
import { SearchSelectInfiniteScroll } from "../../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { endpoints } from "../../../../../../endpoints";
import { useStoreState } from "../../../../../../util/util";
import type { AttributeTemplateSummarySchema } from "../../../../../../types/types.PIM";
import type { OptionType } from "../../../../../../types/types";
import type { EntityType } from "../../../../../../types/types.MDM";
import { useMdmEntityDisplayNames } from "../../../../../../util/mdmEntityDisplayNames";
import {
  PrimaryButtonSmallV2,
  DestructiveButtonSmallV2,
  ButtonContainer,
} from "../../../../../../components/Buttons/Buttons";
import { InfoIcon } from "../../../../../../components/Icons/Icons";
import { HelpDialog } from "../../../../../SharedPages/OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/HelpDialog";
import type { TFunction } from "react-i18next";
import type {
  EntityUploadAction,
  EntityUploadStages,
  EntityUploadState,
} from "./createEntityFromUploads.util";
import { EntityUploadsTable } from "./EntityUploadsTable";
import { DownloadEntityTemplateFiles } from "./DownloadEntityTemplateFiles";
import { UploadEntityTemplateFiles } from "./UploadEntityTemplateFiles";
import {
  TextMdBold,
  TextSmRegular,
} from "../../../../../../components/Typography/TypographyV2";

const EntityUploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
`;

const guidelineOverview = (t: TFunction) => [
  t(
    "Each CSV will come preloaded with the attributes and attribute types for the corresponding groups and collections of the selected template. You can download blank template files which only contain the attributes & attribute types."
  ),
  t(
    "The Groups CSV is used to create new entity profiles and update existing entities, and will be processed first. The Collections CSVs are used for updating existing entities with collection data."
  ),
];

const csvGuidelines = (t: TFunction) => [
  t(
    "Attributes in the headers must match the attribute display names on the template - do not modify the header row."
  ),
  t("Do not change the file name of the CSVs."),
  t(
    "All attributes marked with an asterisk (*) in the header row require a value."
  ),
  t(
    "Do not modify any values in any file within the Entity ID column & leave Entity ID blank when creating a new entity."
  ),
  t(
    "Do not modify any values in the UUID column in the Collections CSVs & leave UUID blank when adding a new collection item."
  ),
  t("Entity Name is required for each row in any CSV."),
  t(
    "The Groups CSV cannot contain duplicate entity names and must only include unique entity names."
  ),
  t(
    "Any changes made to existing values will overwrite the entity data upon upload."
  ),
  t("Entity names in the Collections CSVs must already exist."),
  t(
    "Add existing list values to single / multi-select attributes or create new values."
  ),
  t("Enter only numbers for numeric attributes."),
  t(`Boolean attributes must be either "Yes" or "No".`),
  t("Enter dates in the following format: MMM DD, YYYY"),
  t("Add a new line at the end of the CSV."),
  t("Do not upload an empty group or collection file."),
];

const getStageAndConfirmButtonState = (
  stage?: EntityUploadStages
): Omit<EntityUploadState, "selectedTemplate" | "collectionFileNames"> => {
  switch (stage) {
    case "download":
      return {
        showConfirmTemplateSelectionButton: true,
        uploadStage: "table",
      };
    case "upload":
      return {
        showConfirmTemplateSelectionButton: false,
        uploadStage: "download",
      };
    default:
      return {
        showConfirmTemplateSelectionButton: false,
        uploadStage: "table",
      };
  }
};

const entityUploadReducer = (
  uploadState: EntityUploadState,
  {
    type,
    payload: {
      uploadStage,
      selectedTemplate,
      collectionFileNames,
      export_id,
      selected_entities,
    },
  }: EntityUploadAction
): EntityUploadState => {
  switch (type) {
    case "select_template": {
      if (selectedTemplate) {
        return {
          ...uploadState,
          selectedTemplate,
          showConfirmTemplateSelectionButton: true,
          uploadStage: "table",
          collectionFileNames: {},
          export_id: undefined,
          selected_entities: undefined,
        };
      }
      return uploadState;
    }
    case "confirm_template": {
      return {
        ...uploadState,
        showConfirmTemplateSelectionButton: false,
        uploadStage: "download",
        collectionFileNames: {},
      };
    }
    case "discard_template": {
      return {
        ...uploadState,
        selectedTemplate: undefined,
        showConfirmTemplateSelectionButton: false,
        collectionFileNames: {},
        export_id: undefined,
        selected_entities: undefined,
      };
    }
    case "move_back": {
      return {
        ...uploadState,
        ...getStageAndConfirmButtonState(uploadStage),
      };
    }
    case "set_export_id": {
      return { ...uploadState, export_id };
    }
    case "set_selected_entities": {
      return { ...uploadState, selected_entities };
    }
    case "continue_from_download": {
      return {
        ...uploadState,
        uploadStage: "upload",
        collectionFileNames: collectionFileNames!,
      };
    }
    case "complete": {
      return {
        ...uploadState,
        selectedTemplate: undefined,
        showConfirmTemplateSelectionButton: false,
        uploadStage: "table",
        collectionFileNames: {},
        export_id: undefined,
        selected_entities: undefined,
      };
    }
    default:
      return uploadState;
  }
};

export const CreateEntityFromUploads = ({
  entityType,
}: {
  entityType: EntityType;
}) => {
  const [uploadState, dispatch] = useReducer(entityUploadReducer, {
    uploadStage: "table",
    showConfirmTemplateSelectionButton: false,
    collectionFileNames: {},
  });
  const [showGuide, setShowGuide] = useState(false);
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const theme = useTheme();
  const { getLabel } = useMdmEntityDisplayNames();
  const entityLabelPlural = getLabel(entityType, "plural");

  const handleTemplateSelected = (data: OptionType<string>) => {
    if (uploadState.selectedTemplate?.value !== data.value) {
      dispatch({
        type: "select_template",
        payload: { ...uploadState, selectedTemplate: data },
      });
    }
  };

  const onCancel = () =>
    dispatch({ type: "discard_template", payload: uploadState });

  const onContinue = () =>
    dispatch({ type: "confirm_template", payload: uploadState });

  const getUploadStagesScreens = () => {
    switch (uploadState.uploadStage) {
      case "table":
        return <EntityUploadsTable entityType={entityType} />;
      case "download":
        return (
          <DownloadEntityTemplateFiles
            template={uploadState.selectedTemplate!}
            entityType={entityType}
            export_id={uploadState.export_id}
            selected_entities={uploadState.selected_entities}
            dispatch={dispatch}
          />
        );
      case "upload":
        return (
          <UploadEntityTemplateFiles
            template={uploadState.selectedTemplate!}
            dispatch={dispatch}
            collectionFileNames={uploadState.collectionFileNames}
          />
        );
    }
  };

  return (
    <EntityUploadWrapper>
      <Row style={{ margin: `${theme.resolve("spacing_3xl")} 0` }}>
        <Section style={{ flex: "0.5 0 0" }}>
          <H3 style={{ margin: 0 }}>
            {t("Upload {{label}}", { label: entityLabelPlural })}
          </H3>
          <TextSmRegular
            style={{ margin: `${theme.resolve("spacing_xs")} 0 0` }}
          >
            {t("Select a template for adding {{label}}", {
              label: entityLabelPlural.toLowerCase(),
            })}
          </TextSmRegular>
          <div
            style={{
              marginTop: theme.resolve("spacing_lg"),
              maxWidth: "500px",
            }}
          >
            <SearchSelectInfiniteScroll
              name={"template_search"}
              errors={{}}
              isDisabled={uploadState.uploadStage !== "table"}
              formState={{ submitCount: 0 }}
              placeholder={t("Template name")}
              baseUrl={endpoints.v2_storefronts_id_pim_templates_summary(
                tenant_id
              )}
              params={(() => {
                const params = new URLSearchParams();
                params.append("order_by", "asc");
                params.append("template_type", entityType);
                return params;
              })()}
              getOptions={(response: AttributeTemplateSummarySchema[]) =>
                response.map(({ template_id, template_name }) => ({
                  label: template_name,
                  value: template_id,
                }))
              }
              onChange={handleTemplateSelected}
              value={uploadState.selectedTemplate ?? { value: "", label: "" }}
              testid={"entity-template-search"}
            />
          </div>

          {uploadState.showConfirmTemplateSelectionButton && (
            <ButtonContainer
              style={{
                marginTop: theme.resolve("spacing_3xl"),
                gap: theme.resolve("spacing_lg"),
              }}
            >
              <DestructiveButtonSmallV2 onClick={onCancel} type="reset">
                {t("Cancel")}
              </DestructiveButtonSmallV2>
              <PrimaryButtonSmallV2 onClick={onContinue}>
                {t("Continue")}
              </PrimaryButtonSmallV2>
            </ButtonContainer>
          )}
        </Section>
        <Section
          style={{ alignSelf: "flex-start", gap: theme.resolve("spacing_xs") }}
        >
          <HelpDialog
            show={showGuide}
            setShow={setShowGuide}
            overview={guidelineOverview}
            guidelines={csvGuidelines}
          />
          <TextSmRegular style={{ margin: 0 }}>
            {t("Need help getting started?")}
          </TextSmRegular>
          <div
            onClick={() => setShowGuide(true)}
            style={{
              display: "flex",
              gap: theme.resolve("spacing_xs"),
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <InfoIcon />
            <TextMdBold>{t("View Guide")}</TextMdBold>
          </div>
        </Section>
      </Row>
      {getUploadStagesScreens()}
    </EntityUploadWrapper>
  );
};
